import { useState, useEffect, useRef } from "react";
import { useAuth, useLanguage, API } from "../App";
import { useNavigate } from "react-router-dom";
import { Bell, Check, CheckCheck, Trash2, AlertTriangle, ClipboardCheck, FileText, CheckCircle, Layers } from "lucide-react";

const TYPE_CONFIG = {
  task_completed: { icon: CheckCircle, color: "text-green-500", bg: "bg-green-50" },
  incident_created: { icon: AlertTriangle, color: "text-red-500", bg: "bg-red-50" },
  drawing_version: { icon: Layers, color: "text-blue-500", bg: "bg-blue-50" },
  ra_approved: { icon: FileText, color: "text-purple-500", bg: "bg-purple-50" },
  sr_completed: { icon: ClipboardCheck, color: "text-teal-500", bg: "bg-teal-50" },
  weekly_report: { icon: FileText, color: "text-indigo-500", bg: "bg-indigo-50" },
};

export const NotificationBell = () => {
  const { token } = useAuth();
  const { language } = useLanguage();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(false);
  const ref = useRef(null);
  const sv = (s, e) => language === "sv" ? s : e;
  const hdrs = { Authorization: `Bearer ${token}` };

  // Poll unread count every 30s
  useEffect(() => {
    fetchUnreadCount();
    const interval = setInterval(fetchUnreadCount, 30000);
    return () => clearInterval(interval);
  }, []);

  // Close dropdown on outside click
  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const fetchUnreadCount = async () => {
    try {
      const res = await fetch(`${API}/notifications/unread-count`, { headers: hdrs, credentials: "include" });
      if (res.ok) { const d = await res.json(); setUnreadCount(d.count); }
    } catch {}
  };

  const fetchNotifications = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/notifications?limit=20`, { headers: hdrs, credentials: "include" });
      if (res.ok) { const d = await res.json(); setNotifications(d.notifications); setUnreadCount(d.unread); }
    } catch {}
    finally { setLoading(false); }
  };

  const toggleOpen = () => {
    if (!open) fetchNotifications();
    setOpen(!open);
  };

  const markAsRead = async (notifId) => {
    try {
      await fetch(`${API}/notifications/${notifId}/read`, { method: "PUT", headers: hdrs, credentials: "include" });
      setNotifications(prev => prev.map(n => n.notification_id === notifId ? { ...n, read: true } : n));
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch {}
  };

  const markAllAsRead = async () => {
    try {
      await fetch(`${API}/notifications/read-all`, { method: "PUT", headers: hdrs, credentials: "include" });
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
      setUnreadCount(0);
    } catch {}
  };

  const deleteNotif = async (notifId) => {
    try {
      await fetch(`${API}/notifications/${notifId}`, { method: "DELETE", headers: hdrs, credentials: "include" });
      const wasUnread = notifications.find(n => n.notification_id === notifId && !n.read);
      setNotifications(prev => prev.filter(n => n.notification_id !== notifId));
      if (wasUnread) setUnreadCount(prev => Math.max(0, prev - 1));
    } catch {}
  };

  const handleClick = (notif) => {
    if (!notif.read) markAsRead(notif.notification_id);
    if (notif.link) { navigate(notif.link); setOpen(false); }
  };

  const timeAgo = (dateStr) => {
    const diff = (Date.now() - new Date(dateStr).getTime()) / 1000;
    if (diff < 60) return sv("just nu", "just now");
    if (diff < 3600) return `${Math.floor(diff / 60)} min`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h`;
    return `${Math.floor(diff / 86400)}d`;
  };

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={toggleOpen}
        className="relative p-2 rounded-lg hover:bg-slate-100 transition-colors"
        data-testid="notification-bell"
      >
        <Bell className="w-5 h-5 text-slate-600" />
        {unreadCount > 0 && (
          <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] flex items-center justify-center rounded-full bg-red-500 text-white text-[10px] font-bold px-1" data-testid="notification-badge">
            {unreadCount > 99 ? "99+" : unreadCount}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 mt-2 w-80 sm:w-96 bg-white rounded-xl shadow-xl border border-slate-200 z-50 overflow-hidden" data-testid="notification-dropdown">
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100">
            <h3 className="font-semibold text-sm text-slate-900" style={{ fontFamily: 'Barlow Condensed' }}>
              {sv("Notifikationer", "Notifications")}
              {unreadCount > 0 && <span className="ml-2 text-xs text-slate-400">({unreadCount} {sv("olästa", "unread")})</span>}
            </h3>
            {unreadCount > 0 && (
              <button onClick={markAllAsRead} className="text-xs text-blue-600 hover:text-blue-800 flex items-center gap-1" data-testid="mark-all-read">
                <CheckCheck className="w-3.5 h-3.5" /> {sv("Markera alla", "Mark all")}
              </button>
            )}
          </div>

          {/* List */}
          <div className="max-h-[400px] overflow-y-auto">
            {loading ? (
              <div className="py-8 text-center"><div className="w-5 h-5 border-2 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto" /></div>
            ) : notifications.length === 0 ? (
              <div className="py-8 text-center text-sm text-slate-400">
                <Bell className="w-8 h-8 mx-auto mb-2 text-slate-200" />
                {sv("Inga notifikationer", "No notifications")}
              </div>
            ) : (
              notifications.map(n => {
                const cfg = TYPE_CONFIG[n.type] || TYPE_CONFIG.task_completed;
                const Icon = cfg.icon;
                return (
                  <div
                    key={n.notification_id}
                    className={`flex items-start gap-3 px-4 py-3 border-b border-slate-50 cursor-pointer transition-colors hover:bg-slate-50 ${!n.read ? 'bg-blue-50/40' : ''}`}
                    data-testid={`notification-item-${n.notification_id}`}
                  >
                    <div className={`p-1.5 rounded-lg mt-0.5 flex-shrink-0 ${cfg.bg}`} onClick={() => handleClick(n)}>
                      <Icon className={`w-4 h-4 ${cfg.color}`} />
                    </div>
                    <div className="flex-1 min-w-0" onClick={() => handleClick(n)}>
                      <p className={`text-sm ${!n.read ? 'font-medium text-slate-900' : 'text-slate-600'}`}>{n.title}</p>
                      <p className="text-xs text-slate-500 mt-0.5 truncate">{n.message}</p>
                      <p className="text-[10px] text-slate-400 mt-1">{timeAgo(n.created_at)}</p>
                    </div>
                    <div className="flex items-center gap-0.5 flex-shrink-0">
                      {!n.read && (
                        <button onClick={(e) => { e.stopPropagation(); markAsRead(n.notification_id); }} className="p-1 rounded hover:bg-slate-200" title={sv("Markera som läst", "Mark as read")}>
                          <Check className="w-3.5 h-3.5 text-slate-400" />
                        </button>
                      )}
                      <button onClick={(e) => { e.stopPropagation(); deleteNotif(n.notification_id); }} className="p-1 rounded hover:bg-red-100" title={sv("Radera", "Delete")}>
                        <Trash2 className="w-3.5 h-3.5 text-slate-300 hover:text-red-400" />
                      </button>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
};
